import { CLIENT_ERROR_ROUTES, type ClientErrorSource } from "@nh/shared";

/**
 * Manda al servidor un error que ocurrió AQUÍ, en el navegador.
 *
 * ── Por qué no usa `apiFetch` ────────────────────────────────────────
 * `apiFetch` valida la respuesta contra un esquema y LANZA si algo no
 * cuadra. Usarlo aquí sería poner un lanzador de errores dentro del
 * manejador de errores: un fallo al reportar dispararía otro reporte, y
 * eso es un bucle infinito con el servidor de por medio. Este archivo usa
 * `fetch` pelado y se traga todo: reportar es lo ÚNICO que nunca puede
 * romper la app.
 *
 * ── `keepalive` ──────────────────────────────────────────────────────
 * Muchos errores ocurren justo cuando alguien cierra o recarga la
 * pestaña. Sin `keepalive`, el navegador cancela la petición en vuelo y
 * ese error —el de la salida, que suele ser el interesante— no llega
 * nunca.
 */

/** Techos del contrato compartido. Se recorta aquí para no gastar un 400. */
const MESSAGE_MAX = 500;
const STACK_MAX = 4000;
const PAGE_MAX = 500;

/**
 * Cuántos errores DISTINTOS manda una pestaña como máximo.
 *
 * No es tacañería: es que un error de render en React se repite en cada
 * intento de pintar, y una pestaña abierta toda la tarde puede generar
 * miles. Diez errores distintos ya describen de sobra una sesión rota; del
 * once en adelante no hay información nueva, solo factura de logs. El
 * servidor tiene su propio freno por IP, pero llega tarde: para entonces
 * ya se pagaron las peticiones.
 */
const MAX_POR_SESION = 10;

/** Los que ya se mandaron en esta pestaña, por su huella. */
const yaReportados = new Set<string>();

/**
 * La versión del frontend que está corriendo, inyectada por Vite en la
 * compilación. Sin esto, el error de una pestaña que alguien dejó abierta
 * tres días parece un bug de hoy.
 */
const BUILD = import.meta.env.VITE_BUILD_ID ?? import.meta.env.MODE;

/**
 * La huella de un error: mensaje + primera línea del stack.
 *
 * El stack completo NO sirve como huella —los números de línea de un bundle
 * con hash cambian entre despliegues y el mismo bug parecería nuevo— y solo
 * el mensaje tampoco: "Cannot read properties of undefined" ocurre en veinte
 * lugares distintos y colapsarlos escondería diecinueve bugs.
 */
function huella(mensaje: string, stack?: string): string {
  return `${mensaje}|${stack?.split("\n")[1]?.trim() ?? ""}`;
}

/**
 * Errores que NO son nuestros y que ensucian el log sin decir nada.
 *
 * Las extensiones del navegador, los scripts de terceros y las peticiones
 * que el propio navegador cancela al navegar generan estos a montones. No
 * se pueden arreglar desde aquí, y su ruido entierra los que sí.
 */
const RUIDO = [
  /^ResizeObserver loop/i,
  /^Script error\.?$/i, // error de un script de otro origen: sin detalle, inútil
  /extension:\/\//i,
  /^Load failed$/i, // Safari: la red se cayó o se navegó a otra página
  /^Failed to fetch$/i,
  /^AbortError/i,
  /^The operation was aborted/i,
];

function esRuido(mensaje: string): boolean {
  return RUIDO.some((patron) => patron.test(mensaje));
}

/** Un `throw` puede llevar cualquier cosa encima, no solo un `Error`. */
function describir(valor: unknown): { message: string; stack?: string } {
  if (valor instanceof Error) {
    return { message: valor.message || valor.name, ...(valor.stack ? { stack: valor.stack } : {}) };
  }
  if (typeof valor === "string") return { message: valor };
  try {
    return { message: JSON.stringify(valor) ?? String(valor) };
  } catch {
    return { message: String(valor) };
  }
}

export function reportarError(valor: unknown, source: ClientErrorSource): void {
  try {
    const { message, stack } = describir(valor);
    if (!message.trim() || esRuido(message)) return;

    const clave = huella(message, stack);
    if (yaReportados.has(clave)) return;
    if (yaReportados.size >= MAX_POR_SESION) return;
    yaReportados.add(clave);

    void fetch(CLIENT_ERROR_ROUTES.report, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source,
        message: message.slice(0, MESSAGE_MAX),
        ...(stack ? { stack: stack.slice(0, STACK_MAX) } : {}),
        page: `${window.location.pathname}${window.location.search}`.slice(0, PAGE_MAX),
        build: String(BUILD).slice(0, 60),
      }),
      // Si el reporte falla, falló y ya. Nadie se entera y así debe ser.
    }).catch(() => {});
  } catch {
    /* Reportar jamás rompe la app. Ni siquiera si `window` no existe. */
  }
}

/** Solo para las pruebas: vacía la memoria de lo ya reportado. */
export function _olvidarReportados(): void {
  yaReportados.clear();
}
